import { LitElement, html } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { getProjects, getRecentProjects } from '../../services/project-api.js';
import type { Project } from '../../types/project.js';

export interface ProjectSelectedDetail {
  cwd: string | null;
  projectId: number | null;
}

@customElement('cp-cloud-terminal-project-picker')
export class CpCloudTerminalProjectPicker extends LitElement {
  createRenderRoot() { return this; }

  @state() private _projects: Project[] = [];
  @state() private _loading = true;
  @state() private _error = '';

  override connectedCallback(): void {
    super.connectedCallback();
    this._load();
  }

  private async _load(): Promise<void> {
    try {
      const [recent, all] = await Promise.all([getRecentProjects(), getProjects()]);
      const recentIds = new Set(recent.map(p => p.id));
      const rest = all.filter(p => !recentIds.has(p.id));
      this._projects = [...recent, ...rest];
    } catch (err) {
      this._error = err instanceof Error ? err.message : 'Projekte konnten nicht geladen werden';
    } finally {
      this._loading = false;
    }
  }

  private _select(project: Project): void {
    this.dispatchEvent(new CustomEvent<ProjectSelectedDetail>('project-selected', {
      detail: { cwd: project.path, projectId: project.id },
      bubbles: true, composed: true,
    }));
  }

  private _skip(): void {
    this.dispatchEvent(new CustomEvent<ProjectSelectedDetail>('project-selected', {
      detail: { cwd: null, projectId: null },
      bubbles: true, composed: true,
    }));
  }

  render() {
    return html`
      <div class="cp-project-picker">
        <div class="cp-project-picker__header">Projekt waehlen</div>
        <div class="cp-project-picker__list">
          ${this._loading
            ? html`<div class="cp-project-picker__loading">Lade Projekte...</div>`
            : this._error
              ? html`<div class="cp-project-picker__loading">${this._error}</div>`
              : this._projects.map(p => html`
                <button
                  class="cp-project-picker__item"
                  @click=${() => this._select(p)}
                >
                  <span class="cp-project-picker__name">${p.name}</span>
                  <span class="cp-project-picker__path">${p.path}</span>
                </button>
              `)
          }
        </div>
        <div class="cp-project-picker__footer">
          <button
            class="cp-project-picker__skip"
            @click=${this._skip}
          >
            Kein Projekt / Server-Default
          </button>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cp-cloud-terminal-project-picker': CpCloudTerminalProjectPicker;
  }
}
